import { i18n } from "../boot/i18n";
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { QUOTE_LABOR_TYPES, laborTotalCost } from "./QuoteLaborItem";
import SaleLaborStatus from "./SaleLaborStatus";
import { deepFreeze, formatCurrency, timestampToDate } from "../util/misc";
import { compact, flatten, last, sortBy, uniq, uniqBy } from "lodash";
import { format, startOfToday } from "date-fns";

function getDayString(date) {
  return format(date || startOfToday(), i18n.global.t("format.date-event"));
}

export const SALE_LABOR_FIELDS = {
  line: 0,
  items: [],
  // Cross-sale install: prior-sale line items this labor installs, kept as
  // `{ saleID, itemID }` for traceability back to the sale each item was sold
  // on. Their ids are also merged into `items` at sale creation so scheduling,
  // delivery and billing resolve them like any other item.
  crossSaleItems: [],
  thirdPartyItems: false,
  category: "",
  description: "",
  type: "",
  laborers: [],
  manHours: null,
  quantity: 0,
  margin: null,
  // `cost` is the labor cost only; `materialCost` / `equipmentCost` carry the
  // quoted material and equipment-rental portions over from the quote's labor
  // item (see QUOTE_LABOR_ITEM_FIELDS). Null on sales created before the
  // breakout existed, where the whole `cost` was labor.
  cost: null,
  materialCost: null,
  equipmentCost: null,
  price: null,
  notax: true,
  status: {},

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(SALE_LABOR_FIELDS);

export default class SaleLabor extends DataStructure {
  constructor(projectID, saleID, id, data = {}) {
    // Backward compatibility
    if (!("status" in data) && "quantityReady" in data) {
      data = { ...data };
      if (!data.status) {
        data.status = {};
      }
      data.status.quantityReady = data.quantityReady;
      data.status.quantityCompleted = data.quantityCompleted;
      data.status.hoursScheduled = data.hoursScheduled;
      data.status.hoursCompleted = data.hoursCompleted;
      data.status.hoursInvoiced = data.hoursInvoiced;
      data.status.fullyScheduled = data.fullyScheduled;
      data.status.fullyCompleted = data.fullyCompleted;
      data.status.fullyInvoiced = data.fullyInvoiced;
    }

    data = sanitize(data, SALE_LABOR_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.saleID = saleID || null;
    this.data.status = this.status.data;
    this.permissions = Object.freeze({
      create: this.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  canBeCreatedBy() {
    return false;
  }

  canBeEditedBy(user) {
    const sale = this.sale;
    return user.isEmployee && sale && !sale.locked;
  }

  canBeDeletedBy() {
    return false;
  }

  // Database

  get path() {
    return SaleLabor.getPath(this.projectID, this.saleID, this.id);
  }

  static getPath(projectID, saleID, id) {
    let path = `projects/${projectID}/sales/${saleID}/labor`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(saleLabor) {
    return saleLabor.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new SaleLabor(
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Formatting

  get itemType() {
    return "labor";
  }

  // Mirrors SaleItem.lineNumber. Invoice.items mixes SaleItem, SaleLabor and
  // SaleMiscItem instances, and InvoicePDF renders `item.lineNumber || 0` — so
  // without this getter every labor row printed as line "0" while the material
  // rows printed correctly (sale 03900102A, whose labor is line 11).
  get lineNumber() {
    return this.data.line ? String(this.data.line) : null;
  }

  get project() {
    return this.projectID ? store.state.main.projects[this.projectID] : null;
  }

  get sale() {
    return this.saleID
      ? store.state.main.sales[this.projectID + this.saleID]
      : null;
  }

  get events() {
    const eventsByID = {};

    // Events explicitly tracked on this labor's status (hours scheduled).
    const statusEventIDs = this.data.status.events || {};
    for (const id of Object.keys(statusEventIDs)) {
      const event = store.state.main.events[id];
      if (event) {
        eventsByID[id] = event;
      }
    }

    // Fallback: any loaded installation event whose items reference this labor.
    // `status.events` only records labors with scheduled hours, so a labor
    // assigned 0 hours is omitted there even though the event still lists it in
    // `data.items`. Only installation events store `data.items` as an array
    // (deliveries/pickups use a map), and labor ids are unique per sale, so
    // scanning the loaded events cannot pull in another sale's events.
    for (const event of Object.values(store.state.main.events)) {
      if (
        event?.data &&
        Array.isArray(event.data.items) &&
        event.data.items.includes(this.id)
      ) {
        eventsByID[event.id] = event;
      }
    }

    return sortBy(Object.values(eventsByID), ["start", "end"]);
  }

  get eventInstances() {
    return sortBy(
      flatten(
        this.events.map((event) =>
          event.hasInstances ? event.instances : event
        )
      ),
      ["start", "end"]
    );
  }

  get todaysEvent() {
    const today = getDayString();
    return this.events.find((event) => event.isOngoing(today));
  }

  get nextEventInstance() {
    const now = new Date();
    let hasHappened = false;
    let start, end;
    let instances = this.eventInstances;
    let instance;
    for (let i = 0; i < instances.length; i++) {
      instance = instances[i];
      if (!instance) {
        continue;
      }
      start = instance.start;
      end = instance.end;
      if (start <= now && end > now) {
        // Present
        return instance;
      } else if (end < now) {
        // Past
        hasHappened = true;
      } else if (hasHappened || i === instances.length - 1) {
        // Future
        return instance;
      }
    }
    return null;
  }

  get lastEventInstance() {
    return last(this.eventInstances.reverse());
  }

  get returnDate() {
    const today = getDayString();
    let group = this.instanceGroups.find((group) => group.date > today);
    return group ? group.instances[0].start : null;
  }

  get adjacentLabors() {
    const event = this.nextEventInstance || this.lastEventInstance;
    if (event && event.data && event.data.items.length > 0) {
      return event.items;
    }
    return [this];
  }

  get adjacentLaborItems() {
    return uniqBy(
      flatten(this.adjacentLabors.map((labor) => labor.items)),
      (i) => i.id
    );
  }

  get currentVehicles() {
    const event = this.nextEventInstance || this.lastEventInstance;
    if (!event) {
      return [];
    }
    return event.vehicles;
  }

  get currentParticipants() {
    const event = this.nextEventInstance || this.lastEventInstance;
    if (!event) {
      return [];
    }
    return event.participants;
  }

  get instanceGroups() {
    let instanceGroups = [];
    this.events.forEach((event) => {
      const eventGroups = event.groupedInstances;
      Object.keys(eventGroups).forEach((date) => {
        instanceGroups.push({
          date,
          event,
          instances: eventGroups[date],
        });
      });
    });
    return sortBy(instanceGroups, "date");
  }

  get vehicleIDs() {
    return uniq(flatten(this.events.map((event) => event.data.vehicles)));
  }

  get vehicles() {
    return this.vehicleIDs.map((id) => store.state.main.vehicles[id]);
  }

  get items() {
    return sortBy(
      compact(this.data.items.map((id) => store.state.main.saleItems[id])),
      "line"
    );
  }

  get undeliveredItems() {
    return this.items.filter(
      (item) =>
        !item.fullyInstalled && !item.fullyDelivered && !item.fullyScheduled
    );
  }

  // Undelivered items that actually have received (QC-passed) inventory ready to
  // put on the truck — i.e. more has been received than delivered. Items that
  // are undelivered only because they haven't been received yet are excluded:
  // there is physically nothing to load for them, so they must not force the
  // labor into a "Ready to load" step (sale 04100036A).
  get loadableItems() {
    return this.undeliveredItems.filter(
      (item) => item.data.quantityReady > item.data.quantityDelivered
    );
  }

  get laborers() {
    return compact(
      this.data.laborers.map((id) => store.state.main.contacts[id])
    );
  }

  get cost() {
    return this.data.cost === null ? null : formatCurrency(this.data.cost);
  }

  get materialCost() {
    return this.data.materialCost === null
      ? null
      : formatCurrency(this.data.materialCost);
  }

  get equipmentCost() {
    return this.data.equipmentCost === null
      ? null
      : formatCurrency(this.data.equipmentCost);
  }

  // Labor + materials + equipment rental; the basis for the sales price.
  get totalCost() {
    const total = laborTotalCost(this.data);
    return total === null ? null : formatCurrency(total);
  }

  get price() {
    return this.data.price === null ? null : formatCurrency(this.data.price);
  }

  get total() {
    return formatCurrency(this.data.price === null ? 0 : this.data.price);
  }

  get issues() {
    return sortBy(
      Object.values(store.state.main.issues).filter(
        (issue) =>
          this.id === issue.laborID || this.data.items.includes(issue.itemID)
      ),
      "created"
    ).reverse();
  }

  get adjacentIssues() {
    const laborIDs = this.adjacentLabors.map((l) => l.id);
    const itemIDs = this.adjacentLaborItems.map((i) => i.id);
    return sortBy(
      Object.values(store.state.main.issues).filter(
        (issue) =>
          laborIDs.includes(issue.laborID) || itemIDs.includes(issue.itemID)
      ),
      "created"
    ).reverse();
  }

  get unresolvedIssues() {
    return this.issues.filter((issue) => !issue.isResolved);
  }

  get unresolvedAdjacentIssues() {
    return this.adjacentIssues.filter((issue) => !issue.isResolved);
  }

  get hours() {
    return this.data.manHours;
  }

  get type() {
    return QUOTE_LABOR_TYPES[this.data.type] || null;
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get status() {
    return new SaleLaborStatus(this, this.data.status);
  }

  summary(length = 200) {
    const description = this.data.description.replace(
      /^\*\*+[^*]+\*\*+\s*/gm,
      ""
    );
    return length === null
      ? description
      : description.slice(0, length) + (description.length > length ? "…" : "");
  }

  toString() {
    return this.summary();
  }
}
